import type { OsrmRouteResponse, Route } from '@/js/types/map.ts';
import { makeRequest } from '@/js/api/request-service.ts';
import * as L from 'leaflet';
import markerIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png';

L.Icon.Default.imagePath = '';
L.Icon.Default.mergeOptions({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIconRetinaUrl,
  shadowUrl: markerShadowUrl,
});

const OSRM_URL = 'https://router.project-osrm.org/route/v1/driving';

type Point = { lon: number, lat: number };

export type LeafletMarkerRef = {
  setCoordinates: (coords: number[]) => void
  destroy: () => void
};

export type LeafletLineRef = {
  destroy: () => void
};

export function useLeafletMap() {
  function createMap(element: string | HTMLElement, options: { center: number[], zoom: number }) {
    const [lon, lat] = options.center;
    const map = L.map(element).setView([lat, lon], options.zoom);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    }).addTo(map);
    return map;
  }

  function createMarker(map: L.Map, coordinates: number[]) {
    const [lon, lat] = coordinates;
    const marker = L.marker([lat, lon]).addTo(map);
    return {
      setCoordinates(coords: number[]) {
        const [lng, lt] = coords;
        marker.setLatLng([lt, lng]);
      },
      destroy() {
        marker.remove();
      },
    } satisfies LeafletMarkerRef;
  }

  function createLine(map: L.Map, coordinates: number[][]) {
    const latLngs = coordinates.map(c => [c[1], c[0]] as [number, number]);
    const polyline = L.polyline(latLngs, { color: '#3962b2' }).addTo(map);
    return {
      destroy() {
        map.removeLayer(polyline);
      },
    } satisfies LeafletLineRef;
  }

  async function getCarRoute(start: Point, end: Point) {
    const url = `${OSRM_URL}/${start.lon},${start.lat};${end.lon},${end.lat}?overview=full&geometries=geojson`;
    const response = await makeRequest<OsrmRouteResponse>({ method: 'GET', url });
    if (response.error || !response.data?.routes.length) {
      return { data: { result: [] }, error: response.error };
    }
    const r = response.data.routes[0];
    const route: Route = {
      total_distance: r.distance,
      total_duration: r.duration,
      coordinates: r.geometry.coordinates,
    };
    return { data: { result: [route] }, error: null };
  }

  function createLineByRoute(map: L.Map, route: Route) {
    return createLine(map, route.coordinates);
  }

  return {
    createMap,
    createMarker,
    createLine,
    createLineByRoute,
    getCarRoute,
  };
}
